<template>
  <div class="ops-modern compact">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        @click="backMainPanel"
        class="logo-button"
      />
    </div>

    <div class="tab-content mt-3">
      <div class="text-operations card-strip">
        <div>
          <h2>Órdenes de mantenimiento: {{ totalMaintenanceOrders }}</h2>
        </div>
        <div class="thermometer">
          <div class="bar scheduled" :style="{ width: statusPercent('Pending') }"></div>
          <div class="bar assigned" :style="{ width: statusPercent('Assigned') }"></div>
          <div class="bar active" :style="{ width: statusPercent('Active') }"></div>
          <div class="bar completed" :style="{ width: statusPercent('Completed') }"></div>
        </div>
      </div>

      <div class="action-buttons">
        <button v-if="canAccessOrderMaster" @click="maintenanceMaster" class="action-button">Maestro de Mantenimiento</button>
        <button v-if="canViewTable" @click="openCalendar" class="action-button">Calendario</button>
        <button v-if="canCreateOrder" @click="newMaintenanceOrder" class="action-button action-primary">Nueva orden</button>
        <button @click="backMaintenance" class="action-button">Volver a Mantenimiento</button>
      </div>

      <div class="orders-tabs">
        <button
          class="orders-tab-button"
          :class="{ active: activeTab === 'current' }"
          @click="activeTab = 'current'"
        >
          Ordenes actuales
        </button>
        <button
          class="orders-tab-button"
          :class="{ active: activeTab === 'scheduled' }"
          @click="activeTab = 'scheduled'"
        >
          Ordenes programadas
        </button>
        <button
          class="orders-tab-button"
          :class="{ active: activeTab === 'calendar' }"
          @click="openCalendar"
        >
          Calendario
        </button>
      </div>

      <div v-if="activeTab === 'calendar'" class="card-wrap">
        <div class="calendar-toolbar">
          <button class="action-button" @click="goToPreviousMonth">Mes anterior</button>
          <h2>{{ calendarMonthLabel }}</h2>
          <button class="action-button" @click="goToNextMonth">Mes siguiente</button>
        </div>
        <div v-if="calendarLoading" class="no-access">
          Cargando calendario...
        </div>
        <div v-else class="calendar-grid">
          <div class="calendar-weekday" v-for="weekday in calendarWeekdays" :key="weekday">
            {{ weekday }}
          </div>
          <div
            v-for="day in calendarDays"
            :key="day.dateKey"
            class="calendar-day"
            :class="{ 'calendar-day--outside': !day.isCurrentMonth, 'calendar-day--today': day.dateKey === todayDateString }"
          >
            <div class="calendar-day-number">{{ day.dayNumber }}</div>
            <div class="calendar-day-orders">
              <div
                v-for="order in day.orders"
                :key="order.id"
                class="calendar-order-pill"
                @click="editMaintenanceOrder(order.id)"
                :title="`${order.unit_short_id || 'N/A'} · ${order.motive_of_service || 'N/A'}`"
              >
                {{ order.unit_short_id || 'N/A' }} · {{ order.motive_of_service || 'N/A' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'current'" class="table-container card-wrap">
        <div v-if="!canViewTable" class="no-access">
          No tienes permisos para ver órdenes de mantenimiento.
        </div>
        <h2>Órdenes en proceso</h2>
        <table v-if="canViewTable" class="table table--maintenance">
          <thead>
            <tr>
              <th>
                ID
                <button class="sort-btn" @click="toggleSort('ongoing', 'order_number')">
                  <span v-if="isSorted('ongoing', 'order_number')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Solicitud
                <button class="sort-btn" @click="toggleSort('ongoing', 'request_time')">
                  <span v-if="isSorted('ongoing', 'request_time')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Programada
                <button class="sort-btn" @click="toggleSort('ongoing', 'scheduled_for')">
                  <span v-if="isSorted('ongoing', 'scheduled_for')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Unidad
                <button class="sort-btn" @click="toggleSort('ongoing', 'unit_short_id')">
                  <span v-if="isSorted('ongoing', 'unit_short_id')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Operador
                <button class="sort-btn" @click="toggleSort('ongoing', 'operator_first_name')">
                  <span v-if="isSorted('ongoing', 'operator_first_name')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Mecanico
                <button class="sort-btn" @click="toggleSort('ongoing', 'mechanic_first_name')">
                  <span v-if="isSorted('ongoing', 'mechanic_first_name')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Taller
                <button class="sort-btn" @click="toggleSort('ongoing', 'workshop')">
                  <span v-if="isSorted('ongoing', 'workshop')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Motivo
                <button class="sort-btn" @click="toggleSort('ongoing', 'motive_of_service')">
                  <span v-if="isSorted('ongoing', 'motive_of_service')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Causa
                <button class="sort-btn" @click="toggleSort('ongoing', 'cause')">
                  <span v-if="isSorted('ongoing', 'cause')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Inicio
                <button class="sort-btn" @click="toggleSort('ongoing', 'assignation_start_time')">
                  <span v-if="isSorted('ongoing', 'assignation_start_time')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Salida Aprox.
                <button class="sort-btn" @click="toggleSort('ongoing', 'estimated_end_time')">
                  <span v-if="isSorted('ongoing', 'estimated_end_time')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Estatus
                <button class="sort-btn" @click="toggleSort('ongoing', 'status')">
                  <span v-if="isSorted('ongoing', 'status')">{{ sortState.ongoing.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in ongoingOrders" :key="order.id">
              <td>
                <span class="note-pill" :style="{ backgroundColor: calculateGradient(order.request_time, order.estimated_end_time) }" @click="editMaintenanceOrder(order.id)">
                  {{ order.order_number || order.id }}
                </span>
              </td>
              <td>{{ formatDateTime(order.request_time) }}</td>
              <td>{{ formatDateTime(order.scheduled_for) }}</td>
              <td>{{ order.unit_short_id || 'N/A' }}</td>
              <td>{{ order.operator_full_name || 'N/A' }}</td>
              <td>{{ order.mechanic_full_name || 'N/A' }}</td>
              <td>{{ order.workshop || 'N/A' }}</td>
              <td>{{ order.motive_of_service || 'N/A' }}</td>
              <td>{{ order.cause || 'N/A' }}</td>
              <td>{{ formatTime(order.assignation_start_time || order.start_time) }}</td>
              <td>{{ formatTime(order.estimated_end_time) }}</td>
              <td>
                <span :class="statusClass(order.maintenance_status)">
                  {{ statusLabel(order.maintenance_status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="canViewTable" class="pagination-container">
          <button @click="prevPageOngoing" :disabled="ongoingCurrentPage === 1">
            <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
          </button>
          <span
            v-for="pageNumber in ongoingTotalPages"
            :key="pageNumber"
            @click="goToPageOngoing(pageNumber)"
            :class="{ active: pageNumber === ongoingCurrentPage }"
          >
            {{ pageNumber }}
          </span>
          <button @click="nextPageOngoing" :disabled="ongoingCurrentPage >= ongoingTotalPages">
            <img src="@/assets/angulo-derecho.png" alt="Next Page" />
          </button>
        </div>
      </div>

      <div v-if="activeTab === 'current'" class="table-container card-wrap">
        <h2>Órdenes pendientes</h2>
        <table v-if="canViewTable" class="table table--maintenance">
          <thead>
            <tr>
              <th>
                ID
                <button class="sort-btn" @click="toggleSort('unassigned', 'order_number')">
                  <span v-if="isSorted('unassigned', 'order_number')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Solicitud
                <button class="sort-btn" @click="toggleSort('unassigned', 'request_time')">
                  <span v-if="isSorted('unassigned', 'request_time')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Programada
                <button class="sort-btn" @click="toggleSort('unassigned', 'scheduled_for')">
                  <span v-if="isSorted('unassigned', 'scheduled_for')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Unidad
                <button class="sort-btn" @click="toggleSort('unassigned', 'unit_short_id')">
                  <span v-if="isSorted('unassigned', 'unit_short_id')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Operador
                <button class="sort-btn" @click="toggleSort('unassigned', 'operator_first_name')">
                  <span v-if="isSorted('unassigned', 'operator_first_name')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>Mecanico</th>
              <th>
                Taller
                <button class="sort-btn" @click="toggleSort('unassigned', 'workshop')">
                  <span v-if="isSorted('unassigned', 'workshop')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Motivo
                <button class="sort-btn" @click="toggleSort('unassigned', 'motive_of_service')">
                  <span v-if="isSorted('unassigned', 'motive_of_service')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Causa
                <button class="sort-btn" @click="toggleSort('unassigned', 'cause')">
                  <span v-if="isSorted('unassigned', 'cause')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>Inicio</th>
              <th>
                Salida Aprox.
                <button class="sort-btn" @click="toggleSort('unassigned', 'estimated_end_time')">
                  <span v-if="isSorted('unassigned', 'estimated_end_time')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Estatus
                <button class="sort-btn" @click="toggleSort('unassigned', 'status')">
                  <span v-if="isSorted('unassigned', 'status')">{{ sortState.unassigned.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in unassignedOrders" :key="order.id">
              <td>
                <span class="note-pill" :style="{ backgroundColor: calculateGradient(order.request_time, order.estimated_end_time) }" @click="editMaintenanceOrder(order.id)">
                  {{ order.order_number || order.id }}
                </span>
              </td>
              <td>{{ formatDateTime(order.request_time) }}</td>
              <td>{{ formatDateTime(order.scheduled_for) }}</td>
              <td>{{ order.unit_short_id || 'N/A' }}</td>
              <td>{{ order.operator_full_name || 'N/A' }}</td>
              <td>{{ order.mechanic_full_name || 'N/A' }}</td>
              <td>{{ order.workshop || 'N/A' }}</td>
              <td>{{ order.motive_of_service || 'N/A' }}</td>
              <td>{{ order.cause || 'N/A' }}</td>
              <td>{{ formatTime(order.assignation_start_time || order.start_time) }}</td>
              <td>{{ formatTime(order.estimated_end_time) }}</td>
              <td>
                <span :class="statusClass(order.maintenance_status)">
                  {{ statusLabel(order.maintenance_status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="canViewTable" class="pagination-container">
          <button @click="prevPageUnassigned" :disabled="unassignedCurrentPage === 1">
            <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
          </button>
          <span
            v-for="pageNumber in unassignedTotalPages"
            :key="pageNumber"
            @click="goToPageUnassigned(pageNumber)"
            :class="{ active: pageNumber === unassignedCurrentPage }"
          >
            {{ pageNumber }}
          </span>
          <button @click="nextPageUnassigned" :disabled="unassignedCurrentPage >= unassignedTotalPages">
            <img src="@/assets/angulo-derecho.png" alt="Next Page" />
          </button>
        </div>
      </div>

      <div v-if="activeTab === 'scheduled'" class="table-container card-wrap">
        <h2>Órdenes programadas a futuro</h2>
        <table v-if="canViewTable" class="table table--maintenance">
          <thead>
            <tr>
              <th>
                ID
                <button class="sort-btn" @click="toggleSort('scheduled', 'order_number')">
                  <span v-if="isSorted('scheduled', 'order_number')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Solicitud
                <button class="sort-btn" @click="toggleSort('scheduled', 'request_time')">
                  <span v-if="isSorted('scheduled', 'request_time')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Programada
                <button class="sort-btn" @click="toggleSort('scheduled', 'scheduled_for')">
                  <span v-if="isSorted('scheduled', 'scheduled_for')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Unidad
                <button class="sort-btn" @click="toggleSort('scheduled', 'unit_short_id')">
                  <span v-if="isSorted('scheduled', 'unit_short_id')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Operador
                <button class="sort-btn" @click="toggleSort('scheduled', 'operator_first_name')">
                  <span v-if="isSorted('scheduled', 'operator_first_name')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>Mecanico</th>
              <th>
                Taller
                <button class="sort-btn" @click="toggleSort('scheduled', 'workshop')">
                  <span v-if="isSorted('scheduled', 'workshop')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Motivo
                <button class="sort-btn" @click="toggleSort('scheduled', 'motive_of_service')">
                  <span v-if="isSorted('scheduled', 'motive_of_service')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Causa
                <button class="sort-btn" @click="toggleSort('scheduled', 'cause')">
                  <span v-if="isSorted('scheduled', 'cause')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>Inicio</th>
              <th>
                Salida Aprox.
                <button class="sort-btn" @click="toggleSort('scheduled', 'estimated_end_time')">
                  <span v-if="isSorted('scheduled', 'estimated_end_time')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
              <th>
                Estatus
                <button class="sort-btn" @click="toggleSort('scheduled', 'status')">
                  <span v-if="isSorted('scheduled', 'status')">{{ sortState.scheduled.dir === 'asc' ? '▲' : '▼' }}</span>
                  <span v-else>↕</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in scheduledOrders" :key="order.id">
              <td>
                <span class="note-pill" :style="{ backgroundColor: calculateGradient(order.request_time, order.estimated_end_time) }" @click="editMaintenanceOrder(order.id)">
                  {{ order.order_number || order.id }}
                </span>
              </td>
              <td>{{ formatDateTime(order.request_time) }}</td>
              <td>{{ formatDateTime(order.scheduled_for) }}</td>
              <td>{{ order.unit_short_id || 'N/A' }}</td>
              <td>{{ order.operator_full_name || 'N/A' }}</td>
              <td>{{ order.mechanic_full_name || 'N/A' }}</td>
              <td>{{ order.workshop || 'N/A' }}</td>
              <td>{{ order.motive_of_service || 'N/A' }}</td>
              <td>{{ order.cause || 'N/A' }}</td>
              <td>{{ formatTime(order.assignation_start_time || order.start_time) }}</td>
              <td>{{ formatTime(order.estimated_end_time) }}</td>
              <td>
                <span :class="statusClass(order.maintenance_status)">
                  {{ statusLabel(order.maintenance_status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="canViewTable" class="pagination-container">
          <button @click="prevPageScheduled" :disabled="scheduledCurrentPage === 1">
            <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
          </button>
          <span
            v-for="pageNumber in scheduledTotalPages"
            :key="pageNumber"
            @click="goToPageScheduled(pageNumber)"
            :class="{ active: pageNumber === scheduledCurrentPage }"
          >
            {{ pageNumber }}
          </span>
          <button @click="nextPageScheduled" :disabled="scheduledCurrentPage >= scheduledTotalPages">
            <img src="@/assets/angulo-derecho.png" alt="Next Page" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'UnitMaintenance',
  data() {
    return {
      activeTab: 'current',
      permissions: [],
      ongoingOrders: [],
      unassignedOrders: [],
      scheduledOrders: [],
      ongoingCurrentPage: 1,
      unassignedCurrentPage: 1,
      scheduledCurrentPage: 1,
      ongoingTotalCount: 0,
      unassignedTotalCount: 0,
      scheduledTotalCount: 0,
      ongoingPageSize: 10,
      unassignedPageSize: 10,
      scheduledPageSize: 10,
      statusCounts: {
        Pending: 0,
        Assigned: 0,
        Active: 0,
        Completed: 0,
      },
      calendarBaseDate: new Date(),
      calendarOrders: [],
      calendarLoading: false,
      ongoingFilters: { status: [2, 3] },
      unassignedFilters: { status: [1] },
      sortState: {
        ongoing: { by: '', dir: 'asc' },
        unassigned: { by: '', dir: 'asc' },
        scheduled: { by: '', dir: 'asc' },
      },
      tableConfig: {
        ongoing: { fetch: 'fetchOngoingOrders', pageProp: 'ongoingCurrentPage' },
        unassigned: { fetch: 'fetchUnassignedOrders', pageProp: 'unassignedCurrentPage' },
        scheduled: { fetch: 'fetchScheduledOrders', pageProp: 'scheduledCurrentPage' },
      },
    }
  },
  computed: {
    canViewTable() {
      return this.hasAnyPerm(['maintenance.view_order_table'])
    },
    canCreateOrder() {
      return this.hasAnyPerm(['maintenance.create_order'])
    },
    canAccessOrderMaster() {
      return this.hasAnyPerm(['maintenance.view_order_master'])
    },
    canViewOrder() {
      return this.hasAnyPerm(['maintenance.view_order'])
    },
    totalMaintenanceOrders() {
      return (this.statusCounts.Pending || 0) + (this.statusCounts.Assigned || 0) + (this.statusCounts.Active || 0)
    },
    ongoingTotalPages() {
      return Math.max(1, Math.ceil(this.ongoingTotalCount / this.ongoingPageSize))
    },
    unassignedTotalPages() {
      return Math.max(1, Math.ceil(this.unassignedTotalCount / this.unassignedPageSize))
    },
    scheduledTotalPages() {
      return Math.max(1, Math.ceil(this.scheduledTotalCount / this.scheduledPageSize))
    },
    todayDateString() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    calendarWeekdays() {
      return ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    },
    calendarMonthLabel() {
      const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
      ]
      return `${monthNames[this.calendarBaseDate.getMonth()]} ${this.calendarBaseDate.getFullYear()}`
    },
    calendarMonthStartDate() {
      return new Date(this.calendarBaseDate.getFullYear(), this.calendarBaseDate.getMonth(), 1)
    },
    calendarMonthEndDate() {
      return new Date(this.calendarBaseDate.getFullYear(), this.calendarBaseDate.getMonth() + 1, 0)
    },
    calendarMonthStartString() {
      return this.dateToLocalKey(this.calendarMonthStartDate)
    },
    calendarMonthEndString() {
      return this.dateToLocalKey(this.calendarMonthEndDate)
    },
    calendarOrdersByDate() {
      const grouped = {}
      this.calendarOrders.forEach((order) => {
        const key = this.dateKeyFromIso(order.scheduled_for)
        if (!key) return
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(order)
      })
      return grouped
    },
    calendarDays() {
      const start = this.calendarMonthStartDate
      const end = this.calendarMonthEndDate
      const startWeekDay = start.getDay()
      const daysInMonth = end.getDate()
      const days = []

      for (let i = startWeekDay - 1; i >= 0; i -= 1) {
        const date = new Date(start)
        date.setDate(date.getDate() - (i + 1))
        const key = this.dateToLocalKey(date)
        days.push({
          dateKey: key,
          dayNumber: date.getDate(),
          isCurrentMonth: false,
          orders: this.calendarOrdersByDate[key] || [],
        })
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(this.calendarBaseDate.getFullYear(), this.calendarBaseDate.getMonth(), day)
        const key = this.dateToLocalKey(date)
        days.push({
          dateKey: key,
          dayNumber: day,
          isCurrentMonth: true,
          orders: this.calendarOrdersByDate[key] || [],
        })
      }

      while (days.length % 7 !== 0) {
        const overflowIndex = days.length - (startWeekDay + daysInMonth)
        const date = new Date(end)
        date.setDate(end.getDate() + overflowIndex + 1)
        const key = this.dateToLocalKey(date)
        days.push({
          dateKey: key,
          dayNumber: date.getDate(),
          isCurrentMonth: false,
          orders: this.calendarOrdersByDate[key] || [],
        })
      }
      return days
    },
  },
  mounted() {
    this.loadPermissions()
    if (!this.canViewTable) {
      this.$router.push({ name: 'MainPanel' })
      return
    }
    this.fetchStatusCounts()
    this.fetchOngoingOrders(1)
    this.fetchUnassignedOrders(1)
    this.fetchScheduledOrders(1)
    this.fetchCalendarOrders()
  },
  methods: {
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem('userPermissions')) || []
      } catch (error) {
        this.permissions = []
      }
    },
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    hasAnyPerm(perms) {
      if (!Array.isArray(perms) || perms.length === 0) return false
      return perms.some((perm) => this.hasPerm(perm))
    },
    backMainPanel() {
      this.$router.push({ path: '/main-panel' })
    },
    backMaintenance() {
      this.$router.push({ path: '/modulo-mantenimiento' })
    },
    globalfilters() {
      this.$router.push({ path: '/filtros-globales' })
    },
    maintenanceMaster() {
      if (!this.canAccessOrderMaster) {
        Swal.fire('Sin acceso', 'No tienes permisos para ver el maestro de mantenimiento.', 'warning')
        return
      }
      this.$router.push({ path: '/maestro-mantenimiento' })
    },
    openCalendar() {
      this.activeTab = 'calendar'
      this.fetchCalendarOrders()
    },
    newMaintenanceOrder() {
      this.$router.push({ path: '/maintenance-order-create' })
    },
    editMaintenanceOrder(orderId) {
      if (!this.canViewOrder) {
        Swal.fire('Sin acceso', 'No tienes permisos para ver la orden.', 'warning')
        return
      }
      this.$router.push({ path: `/maintenance-order/${orderId}` })
    },
    statusPercent(status) {
      const total = this.totalMaintenanceOrders
      if (!total) return '0%'
      return `${(this.statusCounts[status] / total) * 100}%`
    },
    statusLabel(status) {
      if (status === 'Pending') return 'Pendiente'
      if (status === 'Assigned') return 'Asignada'
      if (status === 'Active') return 'Activa'
      if (status === 'Completed') return 'Completada'
      return status || 'N/A'
    },
    statusClass(status) {
      if (status === 'Pending') return 'status status--warn'
      if (status === 'Assigned') return 'status status--info'
      if (status === 'Active') return 'status status--ok'
      if (status === 'Completed') return 'status status--ok'
      return 'status'
    },
    formatDateTime(value) {
      if (!value) return 'N/A'
      return `${value.substring(8, 10)}/${value.substring(5, 7)}/${value.substring(0, 4)} ${value.substring(11, 16)}`
    },
    formatTime(value) {
      if (!value) return 'N/A'
      return value.substring(11, 16)
    },
    dateToLocalKey(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    dateKeyFromIso(value) {
      if (!value || typeof value !== 'string') return ''
      return value.substring(0, 10)
    },
    calculateGradient(requestTime, estimatedEndTime) {
      if (!requestTime || !estimatedEndTime) return '#111827'
      const start = new Date(requestTime).getTime()
      const end = new Date(estimatedEndTime).getTime()
      if (!start || !end || end <= start) return '#111827'
      const now = Date.now()
      const progress = Math.min(Math.max((now - start) / (end - start), 0), 1)
      if (progress < 0.33) return '#16a34a'
      if (progress < 0.66) return '#f59e0b'
      return '#ef4444'
    },
    isSorted(tableKey, field) {
      return this.sortState[tableKey].by === field
    },
    orderingFor(tableKey) {
      const state = this.sortState[tableKey]
      if (!state.by) return ''
      const prefix = state.dir === 'desc' ? '-' : ''
      return `&ordering=${prefix}${state.by}`
    },
    toggleSort(tableKey, field) {
      const state = this.sortState[tableKey]
      if (state.by === field) {
        state.dir = state.dir === 'asc' ? 'desc' : 'asc'
      } else {
        state.by = field
        state.dir = 'asc'
      }
      const config = this.tableConfig[tableKey]
      this[config.pageProp] = 1
      const fetchFn = this[config.fetch]
      if (typeof fetchFn === 'function') fetchFn(1)
    },
    async fetchStatusCounts() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        if (!this.canViewTable) {
          return
        }
        const response = await axios.post(`${apiDirectory}/get-maintenance-orders-metadata-statistics`, { status: [1, 2, 3] })
        const baseCounts = { Pending: 0, Assigned: 0, Active: 0, Completed: 0 }
        response.data.forEach((item) => {
          if (item.status !== 'Completed') {
            baseCounts[item.status] = item.count
          }
        })
        this.statusCounts = baseCounts
      } catch (error) {
        console.error('There was an error fetching maintenance statistics', error)
      }
    },
    async fetchOngoingOrders(page = 1) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        if (!this.canViewTable) {
          return
        }
        const orderingParam = this.orderingFor('ongoing')
        const response = await axios.post(
          `${apiDirectory}/get-maintenance-orders-metadata-filtered?page=${page}&page_size=${this.ongoingPageSize}${orderingParam}`,
          {
            ...this.ongoingFilters,
            scheduled_for_end_date: this.todayDateString,
          }
        )
        this.ongoingOrders = response.data.results || []
        this.ongoingTotalCount = response.data.count || 0
      } catch (error) {
        console.error('There was an error fetching ongoing maintenance orders', error)
      }
    },
    async fetchUnassignedOrders(page = 1) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        if (!this.canViewTable) {
          return
        }
        const orderingParam = this.orderingFor('unassigned')
        const response = await axios.post(
          `${apiDirectory}/get-maintenance-orders-metadata-filtered?page=${page}&page_size=${this.unassignedPageSize}${orderingParam}`,
          {
            ...this.unassignedFilters,
            scheduled_for_end_date: this.todayDateString,
          }
        )
        this.unassignedOrders = response.data.results || []
        this.unassignedTotalCount = response.data.count || 0
      } catch (error) {
        console.error('There was an error fetching unassigned maintenance orders', error)
      }
    },
    async fetchScheduledOrders(page = 1) {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        if (!this.canViewTable) {
          return
        }
        const orderingParam = this.orderingFor('scheduled')
        const response = await axios.post(
          `${apiDirectory}/get-maintenance-orders-metadata-filtered?page=${page}&page_size=${this.scheduledPageSize}${orderingParam}`,
          {
            status: [1, 2, 3],
            scheduled_for_after_date: this.todayDateString,
          }
        )
        this.scheduledOrders = response.data.results || []
        this.scheduledTotalCount = response.data.count || 0
      } catch (error) {
        console.error('There was an error fetching scheduled maintenance orders', error)
      }
    },
    async fetchCalendarOrders() {
      const apiDirectory = process.env.API_DIRECTORY
      const pageSize = 100
      try {
        if (!this.canViewTable) {
          return
        }
        this.calendarLoading = true
        let page = 1
        let totalPages = 1
        let aggregated = []
        do {
          const response = await axios.post(
            `${apiDirectory}/get-maintenance-orders-metadata-filtered?page=${page}&page_size=${pageSize}&ordering=scheduled_for`,
            {
              scheduled_for_start_date: this.calendarMonthStartString,
              scheduled_for_end_date: this.calendarMonthEndString,
            }
          )
          const results = response.data.results || []
          const count = response.data.count || 0
          aggregated = aggregated.concat(results)
          totalPages = Math.max(1, Math.ceil(count / pageSize))
          page += 1
        } while (page <= totalPages)
        this.calendarOrders = aggregated.filter((order) => Boolean(order.scheduled_for))
      } catch (error) {
        console.error('There was an error fetching calendar maintenance orders', error)
      } finally {
        this.calendarLoading = false
      }
    },
    prevPageOngoing() {
      if (this.ongoingCurrentPage > 1) {
        this.ongoingCurrentPage -= 1
        this.fetchOngoingOrders(this.ongoingCurrentPage)
      }
    },
    nextPageOngoing() {
      if (this.ongoingCurrentPage < this.ongoingTotalPages) {
        this.ongoingCurrentPage += 1
        this.fetchOngoingOrders(this.ongoingCurrentPage)
      }
    },
    goToPageOngoing(pageNumber) {
      this.ongoingCurrentPage = pageNumber
      this.fetchOngoingOrders(pageNumber)
    },
    prevPageUnassigned() {
      if (this.unassignedCurrentPage > 1) {
        this.unassignedCurrentPage -= 1
        this.fetchUnassignedOrders(this.unassignedCurrentPage)
      }
    },
    nextPageUnassigned() {
      if (this.unassignedCurrentPage < this.unassignedTotalPages) {
        this.unassignedCurrentPage += 1
        this.fetchUnassignedOrders(this.unassignedCurrentPage)
      }
    },
    goToPageUnassigned(pageNumber) {
      this.unassignedCurrentPage = pageNumber
      this.fetchUnassignedOrders(pageNumber)
    },
    prevPageScheduled() {
      if (this.scheduledCurrentPage > 1) {
        this.scheduledCurrentPage -= 1
        this.fetchScheduledOrders(this.scheduledCurrentPage)
      }
    },
    nextPageScheduled() {
      if (this.scheduledCurrentPage < this.scheduledTotalPages) {
        this.scheduledCurrentPage += 1
        this.fetchScheduledOrders(this.scheduledCurrentPage)
      }
    },
    goToPageScheduled(pageNumber) {
      this.scheduledCurrentPage = pageNumber
      this.fetchScheduledOrders(pageNumber)
    },
    goToPreviousMonth() {
      this.calendarBaseDate = new Date(this.calendarBaseDate.getFullYear(), this.calendarBaseDate.getMonth() - 1, 1)
      this.fetchCalendarOrders()
    },
    goToNextMonth() {
      this.calendarBaseDate = new Date(this.calendarBaseDate.getFullYear(), this.calendarBaseDate.getMonth() + 1, 1)
      this.fetchCalendarOrders()
    },
  },
}
</script>

<style scoped>

/* ====== Tokens ====== */
.ops-modern {
  --brand: #ffc434;
  --brand-ink: #3b2b00;
  --ink: #111827;
  --muted: #6b7280;
  --line: #e5e7eb;
  --bg: #f7f7f9;
  --card: #ffffff;
  --radius-lg: 16px;
  --radius-md: 12px;
  --radius-sm: 8px;
  --shadow-1: 0 8px 18px rgba(0,0,0,.06);
  --shadow-2: 0 2px 8px rgba(0,0,0,.08);
}

.ops-modern { background: var(--bg); }

.ops-modern .nav-bar-operations {
  position: sticky; top: 0; left: 0; width: 100%;
  height: 10vh;
  background-color: var(--brand);
  display: flex; align-items: center; justify-content: center;
  box-shadow: var(--shadow-2);
  z-index: 1000;
}
.ops-modern .logo-button { height: 70%; cursor: pointer; }

.card-strip {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-1);
  padding: 10px 14px;
  margin: 8px 12px 12px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: center;
}
.text-operations h2 {
  margin: 0; color: var(--ink); font-size: 1.1rem; font-weight: 800;
}
.status-counts {
  text-align: left; font-size: .85rem; color: var(--muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.thermometer {
  height: 14px;
  background: #eef2f7;
  border-radius: 999px;
  display: flex; overflow: hidden;
  border: 1px solid var(--line);
}
.bar { height: 100%; }
.bar.active { background: #16a34a; }
.bar.assigned { background: #facc15; }
.bar.scheduled { background: #8a2be2; }
.bar.completed { background: #2563eb; }

.action-buttons {
  display: flex; justify-content: flex-end; gap: 8px;
  margin: 10px 12px 0;
}
.action-button {
  padding: 10px 14px; border: 1px solid var(--line);
  background: #111827; color: #fff; border-radius: var(--radius-md);
  font-weight: 700; box-shadow: var(--shadow-2);
  transition: transform .12s, box-shadow .12s, opacity .12s;
}
.action-button:hover { transform: translateY(-1px); }

.orders-tabs {
  display: flex;
  gap: 8px;
  margin: 12px 12px 0;
}

.orders-tab-button {
  padding: 8px 12px;
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  font-weight: 700;
}

.orders-tab-button.active {
  background: var(--brand);
  border-color: transparent;
  color: var(--brand-ink);
}

.calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.calendar-toolbar h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}

.calendar-weekday {
  font-size: .8rem;
  font-weight: 700;
  color: var(--muted);
  text-align: center;
  padding: 4px 0;
}

.calendar-day {
  min-height: 120px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 6px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.calendar-day--outside {
  background: #f8fafc;
  opacity: .75;
}

.calendar-day--today {
  border-color: var(--brand);
  box-shadow: inset 0 0 0 1px var(--brand);
}

.calendar-day-number {
  font-size: .8rem;
  font-weight: 800;
  color: var(--ink);
}

.calendar-day-orders {
  display: grid;
  gap: 4px;
}

.calendar-order-pill {
  font-size: .72rem;
  font-weight: 700;
  background: #eef3ff;
  color: #134c9a;
  border: 1px solid #c6ddff;
  border-radius: 999px;
  padding: 3px 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.card-wrap {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-1);
  padding: 12px;
  margin: 12px;
}
.card-wrap > h2 {
  margin: 4px 6px 10px; font-size: 1rem; font-weight: 800; color: var(--ink);
}

.table-container { overflow-x: auto; }
.table {
  width: 100%;
  border-collapse: separate; border-spacing: 0;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-1);
  table-layout: fixed;
  font-size: .85rem;
}
.table thead th {
  position: sticky; top: 0; z-index: 1;
  background: #f5f6f8;
  color: #475163;
  font-weight: 600;
  border-bottom: 1px solid var(--border);
  padding: 6px 8px;
  white-space: nowrap;
}
.table tbody td {
  padding: 10px 12px; border-bottom: 1px solid var(--line);
  color: var(--ink); vertical-align: top; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.table tbody tr:nth-child(even) td { background: #fcfcfd; }
.table tbody tr:hover td { background: #eef3ff; color: #0f172a; }

.table--maintenance td:nth-child(1), .table--maintenance th:nth-child(1) { width: 90px; }
.table--maintenance td:nth-child(2), .table--maintenance th:nth-child(2) { width: 170px; }
.table--maintenance td:nth-child(3), .table--maintenance th:nth-child(3) { width: 110px; }
.table--maintenance td:nth-child(4), .table--maintenance th:nth-child(4) { width: 160px; }
.table--maintenance td:nth-child(5), .table--maintenance th:nth-child(5) { width: 160px; }
.table--maintenance td:nth-child(6), .table--maintenance th:nth-child(6) { width: 140px; }
.table--maintenance td:nth-child(7), .table--maintenance th:nth-child(7) { width: 140px; }
.table--maintenance td:nth-child(8), .table--maintenance th:nth-child(8) { width: 110px; }
.table--maintenance td:nth-child(9), .table--maintenance th:nth-child(9) { width: 120px; }
.table--maintenance td:nth-child(10), .table--maintenance th:nth-child(10) { width: 120px; }
.table--maintenance td:nth-child(11), .table--maintenance th:nth-child(11) { width: 120px; }

.note-pill {
  display: inline-block; color: #fff; font-weight: 800;
  padding: 3px 8px; border-radius: 999px;
  cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.15);
}

.status {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px; font-weight: 800; font-size: .85rem;
  border: 1px solid transparent;
}
.status--ok { background: #e8f7ef; color: #0f7a3b; border-color: #bde6cd; }
.status--warn { background: #fff6e5; color: #8a6100; border-color: #f2dfb2; }
.status--info { background: #eaf3ff; color: #134c9a; border-color: #c6ddff; }

.sort-btn {
  margin-left: 6px; border: 0; background: transparent;
  cursor: pointer; font-size: .8rem; color: #6b7280;
}

.pagination-container {
  display: flex; justify-content: center; align-items: center; gap: 10px;
  margin: 12px 0 4px;
}
.pagination-container span {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 6px 10px; border-radius: 10px; cursor: pointer;
  border: 1px solid var(--line); background: #fff;
}
.pagination-container span.active {
  background: var(--brand); color: var(--brand-ink); border-color: transparent;
}
.pagination-container button {
  border: 1px solid var(--line); background: #fff; border-radius: 10px;
  padding: 4px 8px; cursor: pointer;
}
.pagination-container button:disabled { opacity: .4; cursor: not-allowed; }

.no-access {
  text-align: center;
  padding: 18px 12px;
  color: #8a3b00;
  background: #fff6e5;
  border: 1px solid #f2dfb2;
  border-radius: 10px;
  margin-bottom: 12px;
}
</style>
